import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { verificationAPI } from '../services/api';

interface VerificationResult {
  isAuthentic: boolean;
  isCounterfeit: boolean;
  product?: {
    name: string;
    brand: string;
    batchId: string;
    productionDate: string;
    alcoholContent?: number;
    volumeMl?: number;
    description?: string;
  };
  manufacturer?: {
    name: string;
    licenseNumber: string;
  };
  reason?: string;
  verificationTime?: string;
}

interface Product {
  id: string;
  name: string;
  manufacturer: string;
  category: string;
  barcode: string;
}

const VerifyPage: React.FC = () => {
  const { publicToken } = useParams<{ publicToken: string }>();
  const [step, setStep] = useState(1);
  const [barcode, setBarcode] = useState('');
  const [batchCode, setBatchCode] = useState('');
  const [manualToken, setManualToken] = useState('');
  const [product, setProduct] = useState<Product | null>(null);
  const [result, setResult] = useState<VerificationResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isScanning, setIsScanning] = useState(false);

  // Animation styles
  const animationStyles = `
    .slide-up {
      animation: slideUp 0.6s cubic-bezier(0.4, 0, 0.2, 1) forwards;
    }
    
    .fade-in {
      animation: fadeIn 0.8s ease-out forwards;
    }
    
    .scale-in {
      animation: scaleIn 0.5s cubic-bezier(0.4, 0, 0.2, 1) forwards;
    }
    
    .pulse-ring {
      animation: pulseRing 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
    }
    
    @keyframes slideUp {
      from {
        opacity: 0;
        transform: translateY(40px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
    
    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    
    @keyframes scaleIn {
      from {
        opacity: 0;
        transform: scale(0.9);
      }
      to {
        opacity: 1;
        transform: scale(1);
      }
    }
    
    @keyframes pulseRing {
      0% {
        transform: scale(0.33);
        opacity: 1;
      }
      80%, 100% {
        transform: scale(2.33);
        opacity: 0;
      }
    }
    
    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
    
    .scanner-animation {
      position: relative;
      overflow: hidden;
    }
    
    .scanner-line {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 2px;
      background: linear-gradient(90deg, transparent, #3b82f6, transparent);
      animation: scanLine 2s ease-in-out infinite;
    }
    
    @keyframes scanLine {
      0% { transform: translateY(0); }
      100% { transform: translateY(200px); }
    }
  `;

  // Handle URL token verification on mount
  useEffect(() => {
    if (publicToken) {
      handleTokenVerification(publicToken);
    }
  }, [publicToken]);

  // Original token verification function
  const handleTokenVerification = async (token: string) => {
    if (!token.trim()) {
      setError('Please enter a verification token');
      return;
    }

    setIsLoading(true);
    setError('');
    setResult(null);

    try {
      const response = await verificationAPI.verify(token.trim());
      setResult(response.data.data);
      setStep(3); // Go directly to results
    } catch (error: any) {
      setError(error.response?.data?.message || 'Verification failed');
    }
    setIsLoading(false);
  };

  const handleManualTokenVerification = (e: React.FormEvent) => {
    e.preventDefault();
    handleTokenVerification(manualToken);
  };

  // New barcode scanning function
  const handleBarcodeScanned = async (scannedBarcode: string) => {
    setBarcode(scannedBarcode);
    
    
    try {
      // Simulate API call for product lookup
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Mock product data - replace with actual API call
      const mockProduct: Product = {
        id: '1',
        name: 'Premium Product 500ml',
        manufacturer: 'Trusted Manufacturer Ltd',
        category: 'Beverages',
        barcode: scannedBarcode
      };
      
      setProduct(mockProduct);
      setStep(2);
    } catch (error) {
      setError('Product lookup failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // New batch verification function
  const handleBatchVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!batchCode.trim()) return;

    setIsLoading(true);
    setError('');
    
    try {
      // Create a token-like verification for batch + barcode
      const combinedToken = `${barcode}-${batchCode.trim()}`;
      
      // Try to use existing API or simulate
      try {
        const response = await verificationAPI.verify(combinedToken);
        setResult(response.data.data);
      } catch (apiError) {
        // Fallback to mock data if API doesn't support new format yet
        const mockResult: VerificationResult = {
          isAuthentic: Math.random() > 0.3,
          isCounterfeit: Math.random() <= 0.3,
          product: {
            name: product?.name || 'Unknown Product',
            brand: product?.manufacturer || 'Unknown Brand',
            batchId: batchCode.trim(),
            productionDate: new Date().toISOString(),
            description: 'Verified through ScanOnce 2.0'
          },
          manufacturer: {
            name: product?.manufacturer || 'Unknown Manufacturer',
            licenseNumber: 'LIC-' + Math.random().toString(36).substr(2, 9).toUpperCase()
          },
          verificationTime: new Date().toISOString()
        };
        setResult(mockResult);
      }
      
      setStep(3);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Verification failed');
    } finally {
      setIsLoading(false);
    }
  };

  const resetVerification = () => {
    setStep(1);
    setBarcode('');
    setBatchCode('');
    setManualToken('');
    setProduct(null);
    setResult(null);
    setError('');
    setIsScanning(false);
  };

  const simulateBarcodeScan = () => {
    setIsScanning(true);
    setTimeout(() => {
      handleBarcodeScanned('1234567890123');
      setIsScanning(false);
    }, 3000);
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)',
      position: 'relative',
      overflow: 'hidden'
    }}>
      <style>{animationStyles}</style>
      
      {/* Background Pattern */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: `radial-gradient(circle at 20% 80%, rgba(59, 130, 246, 0.1) 0%, transparent 50%),
                         radial-gradient(circle at 80% 20%, rgba(6, 182, 212, 0.1) 0%, transparent 50%),
                         radial-gradient(circle at 40% 40%, rgba(139, 92, 246, 0.1) 0%, transparent 50%)`,
      }}></div>

      <div style={{
        position: 'relative',
        zIndex: 1,
        padding: '40px 20px',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          maxWidth: '800px',
          width: '100%'
        }}>
          {/* Header */}
          <div className="fade-in" style={{
            textAlign: 'center',
            marginBottom: '60px'
          }}>
            <div style={{
              display: 'inline-block',
              padding: '8px 24px',
              background: 'rgba(59, 130, 246, 0.1)',
              border: '1px solid rgba(59, 130, 246, 0.2)',
              borderRadius: '50px',
              marginBottom: '24px'
            }}>
              <span style={{
                color: '#3b82f6',
                fontSize: '14px',
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Universal Product Verification
              </span>
            </div>

            <h1 style={{
              fontSize: '3.5rem',
              fontWeight: '800',
              color: 'white',
              marginBottom: '24px',
              lineHeight: '1.1',
              letterSpacing: '-0.02em'
            }}>
              Verify Product{' '}
              <span style={{
                background: 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
                Authenticity
              </span>
            </h1>

            <p style={{
              fontSize: '20px',
              color: '#94a3b8',
              maxWidth: '600px',
              margin: '0 auto',
              lineHeight: '1.6'
            }}>
              Scan any product barcode and verify authenticity in seconds. 
              Protect yourself from counterfeits with enterprise-grade verification.
            </p>
          </div>

          {/* Error Display */}
          {error && (
            <div className="slide-up" style={{
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.2)',
              borderRadius: '16px',
              padding: '20px',
              marginBottom: '30px',
              backdropFilter: 'blur(10px)'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="15" y1="9" x2="9" y2="15"/>
                    <line x1="9" y1="9" x2="15" y2="15"/>
                  </svg>
                </div>
                <div>
                  <h4 style={{ color: '#ef4444', margin: '0 0 4px 0', fontSize: '16px', fontWeight: '600' }}>
                    Verification Failed
                  </h4>
                  <p style={{ color: '#fca5a5', margin: 0, fontSize: '14px' }}>
                    {error}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Step 1: Barcode Scanner or Token Input */}
          {step === 1 && !result && (
            <div className="slide-up" style={{
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '24px',
              padding: '48px',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                marginBottom: '32px'
              }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '20px',
                  fontWeight: '700'
                }}>
                  1
                </div>
                <h2 style={{
                  color: '#1e293b',
                  fontSize: '28px',
                  fontWeight: '700',
                  margin: 0
                }}>
                  Choose Verification Method
                </h2>
              </div>

              {/* Method Selection Tabs */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: '24px',
                marginBottom: '32px'
              }}>
                {/* Barcode Scanner Method */}
                <div style={{
                  background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                  border: '2px solid #e2e8f0',
                  borderRadius: '16px',
                  padding: '32px',
                  textAlign: 'center'
                }} className={isScanning ? 'scanner-animation' : ''}>
                  {isScanning && <div className="scanner-line"></div>}
                  
                  <div style={{
                    width: '80px',
                    height: '80px',
                    background: isScanning 
                      ? 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)'
                      : 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
                    borderRadius: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 20px',
                    transition: 'all 0.3s ease',
                    position: 'relative'
                  }}>
                    {isScanning && (
                      <div style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '80px',
                        height: '80px',
                        border: '3px solid #22c55e',
                        borderRadius: '20px',
                        opacity: 0.6
                      }} className="pulse-ring"></div>
                    )}
                    
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                      <circle cx="12" cy="13" r="4"/>
                    </svg>
                  </div>

                  <h3 style={{
                    color: '#1e293b',
                    fontSize: '18px',
                    fontWeight: '600',
                    marginBottom: '12px'
                  }}>
                    {isScanning ? 'Scanning Product...' : 'Scan Barcode'}
                  </h3>

                  <p style={{
                    color: '#64748b',
                    fontSize: '14px',
                    marginBottom: '20px',
                    lineHeight: '1.5'
                  }}>
                    {isScanning 
                      ? 'Please wait while we identify your product'
                      : 'Use camera to scan product barcode for universal verification'
                    }
                  </p>

                  <button
                    onClick={simulateBarcodeScan}
                    disabled={isScanning || isLoading}
                    style={{
                      background: isScanning 
                        ? 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)'
                        : 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
                      color: 'white',
                      padding: '12px 24px',
                      border: 'none',
                      borderRadius: '10px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: isScanning || isLoading ? 'not-allowed' : 'pointer',
                      transition: 'all 0.3s ease',
                      opacity: isScanning || isLoading ? 0.7 : 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      margin: '0 auto'
                    }}
                  >
                    {isScanning ? (
                      <>
                        <div style={{
                          width: '14px',
                          height: '14px',
                          border: '2px solid rgba(255, 255, 255, 0.3)',
                          borderTop: '2px solid white',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite'
                        }}></div>
                        Scanning...
                      </>
                    ) : (
                      'Start Camera Scan'
                    )}
                  </button>
                </div>

                {/* Token Input Method */}
                <div style={{
                  background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                  border: '2px solid #e2e8f0',
                  borderRadius: '16px',
                  padding: '32px',
                  textAlign: 'center'
                }}>
                  <div style={{
                    width: '80px',
                    height: '80px',
                    background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                    borderRadius: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 20px'
                  }}>
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                      <line x1="16" y1="2" x2="16" y2="6"/>
                      <line x1="8" y1="2" x2="8" y2="6"/>
                      <line x1="3" y1="10" x2="21" y2="10"/>
                    </svg>
                  </div>

                  <h3 style={{
                    color: '#1e293b',
                    fontSize: '18px',
                    fontWeight: '600',
                    marginBottom: '12px'
                  }}>
                    Enter Token
                  </h3>

                  <p style={{
                    color: '#64748b',
                    fontSize: '14px',
                    marginBottom: '20px',
                    lineHeight: '1.5'
                  }}>
                    Have a verification token? Enter it directly for instant results
                  </p>

                  <form onSubmit={handleManualTokenVerification}>
                    <input
                      type="text"
                      value={manualToken}
                      onChange={(e) => setManualToken(e.target.value)}
                      placeholder="Enter verification token"
                      disabled={isLoading}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: '2px solid #e2e8f0',
                        borderRadius: '10px',
                        fontSize: '14px',
                        outline: 'none',
                        marginBottom: '16px',
                        fontFamily: 'monospace'
                      }}
                    />
                    <button
                      type="submit"
                      disabled={isLoading || !manualToken.trim()}
                      style={{
                        background: isLoading || !manualToken.trim()
                          ? '#9ca3af'
                          : 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                        color: 'white',
                        padding: '12px 24px',
                        border: 'none',
                        borderRadius: '10px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: isLoading || !manualToken.trim() ? 'not-allowed' : 'pointer',
                        width: '100%'
                      }}
                    >
                      {isLoading ? 'Verifying...' : 'Verify Token'}
                    </button>
                  </form>
                </div>
              </div>

              {/* Manual Barcode Entry */}
              <div style={{
                borderTop: '1px solid #e2e8f0',
                paddingTop: '32px'
              }}>
                <h4 style={{
                  color: '#374151',
                  fontSize: '16px',
                  fontWeight: '600',
                  marginBottom: '16px',
                  textAlign: 'center'
                }}>
                  Or Enter Barcode Manually
                </h4>
                
                <form onSubmit={(e) => {
                  e.preventDefault();
                  if (barcode.trim()) {
                    handleBarcodeScanned(barcode.trim());
                  }
                }}>
                  <div style={{
                    display: 'flex',
                    gap: '16px',
                    maxWidth: '500px',
                    margin: '0 auto'
                  }}>
                    <input
                      type="text"
                      value={barcode}
                      onChange={(e) => setBarcode(e.target.value)}
                      placeholder="Enter 13-digit barcode"
                      disabled={isLoading}
                      style={{
                        flex: 1,
                        padding: '12px 16px',
                        border: '2px solid #e2e8f0',
                        borderRadius: '10px',
                        fontSize: '14px',
                        outline: 'none',
                        fontFamily: 'monospace'
                      }}
                    />
                    <button
                      type="submit"
                      disabled={isLoading || !barcode.trim()}
                      style={{
                        background: isLoading || !barcode.trim() 
                          ? '#9ca3af' 
                          : 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
                        color: 'white',
                        padding: '12px 20px',
                        border: 'none',
                        borderRadius: '10px',
                        fontWeight: '600',
                        fontSize: '14px',
                        cursor: isLoading || !barcode.trim() ? 'not-allowed' : 'pointer'
                      }}
                    >
                      Scan
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Step 2: Batch Code Entry */}
          {step === 2 && product && (
            <div className="slide-up" style={{
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '24px',
              padding: '48px',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                marginBottom: '32px'
              }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '20px',
                  fontWeight: '700'
                }}>
                  2
                </div>
                <h2 style={{
                  color: '#1e293b',
                  fontSize: '28px',
                  fontWeight: '700',
                  margin: 0
                }}>
                  Enter Batch Code
                </h2>
              </div>

              {/* Product Found */}
              <div style={{
                background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
                border: '1px solid #bbf7d0',
                borderRadius: '16px',
                padding: '24px',
                marginBottom: '32px'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px'
                }}>
                  <div style={{
                    width: '60px',
                    height: '60px',
                    background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                      <polyline points="20,6 9,17 4,12"/>
                    </svg>
                  </div>
                  <div>
                    <h3 style={{
                      color: '#166534',
                      fontSize: '20px',
                      fontWeight: '600',
                      margin: '0 0 4px 0'
                    }}>
                      Product Found: {product.name}
                    </h3>
                    <p style={{
                      color: '#15803d',
                      fontSize: '14px',
                      margin: '0 0 4px 0'
                    }}>
                      Manufacturer: {product.manufacturer}
                    </p>
                    <p style={{
                      color: '#15803d',
                      fontSize: '14px',
                      margin: 0
                    }}>
                      Category: {product.category} • Barcode: {product.barcode}
                    </p>
                  </div>
                </div>
              </div>

              <form onSubmit={handleBatchVerification}>
                <div style={{ marginBottom: '24px' }}>
                  <label style={{
                    display: 'block',
                    color: '#374151',
                    fontWeight: '600',
                    marginBottom: '12px',
                    fontSize: '16px'
                  }}>
                    Batch Code (found on product packaging)
                  </label>
                  <input
                    type="text"
                    value={batchCode}
                    onChange={(e) => setBatchCode(e.target.value)}
                    placeholder="e.g., CC240315, LOT123, BATCH001"
                    required
                    disabled={isLoading}
                    style={{
                      width: '100%',
                      padding: '20px 24px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '12px',
                      fontSize: '18px',
                      outline: 'none',
                      transition: 'all 0.3s ease',
                      fontFamily: 'monospace',
                      letterSpacing: '1px'
                    }}
                  />
                  <p style={{
                    color: '#64748b',
                    fontSize: '14px',
                    marginTop: '8px',
                    lineHeight: '1.5'
                  }}>
                    Look for the batch code printed on the product label, usually near the manufacturing or expiry date.
                  </p>
                </div>
                
                <div style={{
                  display: 'flex',
                  gap: '16px',
                  justifyContent: 'flex-end'
                }}>
                  <button
                    type="button"
                    onClick={resetVerification}
                    style={{
                      backgroundColor: '#f1f5f9',
                      color: '#475569',
                      padding: '16px 24px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '12px',
                      fontWeight: '600',
                      fontSize: '16px',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    Start Over
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading || !batchCode.trim()}
                    style={{
                      background: isLoading || !batchCode.trim()
                        ? '#9ca3af'
                        : 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
                      color: 'white',
                      padding: '16px 32px',
                      border: 'none',
                      borderRadius: '12px',
                      fontWeight: '600',
                      fontSize: '16px',
                                           cursor: isLoading || !batchCode.trim() ? 'not-allowed' : 'pointer',
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      minWidth: '180px',
                      justifyContent: 'center'
                    }}
                  >
                    {isLoading ? (
                      <>
                        <div style={{
                          width: '20px',
                          height: '20px',
                          border: '2px solid rgba(255, 255, 255, 0.3)',
                          borderTop: '2px solid white',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite'
                        }}></div>
                        Verifying...
                      </>
                    ) : (
                      <>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M9 12l2 2 4-4"/>
                          <circle cx="12" cy="12" r="9"/>
                        </svg>
                        Verify Authenticity
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Step 3: Results (Enhanced with original functionality) */}
          {(step === 3 || result) && result && (
            <div className="scale-in" style={{
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '24px',
              padding: '48px',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                marginBottom: '40px'
              }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  background: result.isAuthentic 
                    ? 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)'
                    : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '20px',
                  fontWeight: '700'
                }}>
                  3
                </div>
                <h2 style={{
                  color: '#1e293b',
                  fontSize: '28px',
                  fontWeight: '700',
                  margin: 0
                }}>
                  Verification Complete
                </h2>
              </div>

              {result.isAuthentic ? (
                // AUTHENTIC PRODUCT - Enhanced Design
                <div>
                  <div style={{
                    background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
                    border: '2px solid #22c55e',
                    borderRadius: '20px',
                    padding: '40px',
                    textAlign: 'center',
                    marginBottom: '32px'
                  }}>
                    <div style={{
                      width: '120px',
                      height: '120px',
                      background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                      borderRadius: '30px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 24px',
                      position: 'relative'
                    }}>
                      <div style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '120px',
                        height: '120px',
                        border: '3px solid #22c55e',
                        borderRadius: '30px',
                        opacity: 0.3
                      }} className="pulse-ring"></div>
                      
                      <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                        <polyline points="20,6 9,17 4,12"/>
                      </svg>
                    </div>

                    <h2 style={{
                      color: '#15803d',
                      fontSize: '2rem',
                      fontWeight: '800',
                      marginBottom: '12px',
                      letterSpacing: '-0.02em'
                    }}>
                      AUTHENTIC PRODUCT
                    </h2>
                    <p style={{
                      color: '#166534',
                      fontSize: '18px',
                      fontWeight: '500',
                      marginBottom: '16px'
                    }}>
                      This product has been verified as genuine and safe
                    </p>
                    <div style={{
                      display: 'inline-block',
                      padding: '8px 20px',
                      background: 'rgba(34, 197, 94, 0.1)',
                      border: '1px solid rgba(34, 197, 94, 0.2)',
                      borderRadius: '50px',
                      color: '#15803d',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}>
                      Verified by ScanOnce Enterprise
                    </div>
                  </div>

                  {result.product && (
                    <div style={{
                      background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                      borderRadius: '16px',
                      padding: '32px',
                      marginBottom: '24px'
                    }}>
                      <h3 style={{
                        color: '#1e293b',
                        fontSize: '20px',
                        fontWeight: '600',
                        marginBottom: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/>
                        </svg>
                        Product Details
                      </h3>
                      
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                        gap: '20px'
                      }}>
                        <div style={{
                          background: 'white',
                          padding: '20px',
                          borderRadius: '12px',
                          border: '1px solid #e2e8f0'
                        }}>
                          <div style={{ color: '#64748b', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', marginBottom: '4px' }}>
                            Product Name
                          </div>
                          <div style={{ color: '#1e293b', fontSize: '16px', fontWeight: '600' }}>
                            {result.product.name}
                          </div>
                        </div>

                        <div style={{
                          background: 'white',
                          padding: '20px',
                          borderRadius: '12px',
                          border: '1px solid #e2e8f0'
                        }}>
                          <div style={{ color: '#64748b', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', marginBottom: '4px' }}>
                            Brand
                          </div>
                          <div style={{ color: '#1e293b', fontSize: '16px', fontWeight: '600' }}>
                            {result.product.brand}
                          </div>
                        </div>

                        <div style={{
                          background: 'white',
                          padding: '20px',
                          borderRadius: '12px',
                          border: '1px solid #e2e8f0'
                        }}>
                          <div style={{ color: '#64748b', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', marginBottom: '4px' }}>
                            Batch ID
                          </div>
                          <div style={{ color: '#1e293b', fontSize: '16px', fontWeight: '600', fontFamily: 'monospace' }}>
                            {result.product.batchId}
                          </div>
                        </div>

                        <div style={{
                          background: 'white',
                          padding: '20px',
                          borderRadius: '12px',
                          border: '1px solid #e2e8f0'
                        }}>
                          <div style={{ color: '#64748b', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', marginBottom: '4px' }}>
                            Production Date
                          </div>
                          <div style={{ color: '#1e293b', fontSize: '16px', fontWeight: '600' }}>
                            {new Date(result.product.productionDate).toLocaleDateString()}
                          </div>
                        </div>

                        {result.product.alcoholContent && (
                          <div style={{
                            background: 'white',
                            padding: '20px',
                            borderRadius: '12px',
                            border: '1px solid #e2e8f0'
                          }}>
                            <div style={{ color: '#64748b', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', marginBottom: '4px' }}>
                              Alcohol Content
                            </div>
                            <div style={{ color: '#1e293b', fontSize: '16px', fontWeight: '600' }}>
                              {result.product.alcoholContent}%
                            </div>
                          </div>
                        )}

                        {result.product.volumeMl && (
                          <div style={{
                            background: 'white',
                            padding: '20px',
                            borderRadius: '12px',
                            border: '1px solid #e2e8f0'
                          }}>
                            <div style={{ color: '#64748b', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', marginBottom: '4px' }}>
                              Volume
                            </div>
                            <div style={{ color: '#1e293b', fontSize: '16px', fontWeight: '600' }}>
                              {result.product.volumeMl}ml
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {result.manufacturer && (
                    <div style={{
                      background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                      border: '1px solid #3b82f6',
                      borderRadius: '16px',
                      padding: '24px',
                      marginBottom: '24px'
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px'
                      }}>
                        <div style={{
                          width: '50px',
                          height: '50px',
                          background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
                          borderRadius: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                            <path d="M3 21h18"/>
                            <path d="M5 21V7l8-4v18"/>
                            <path d="M19 21V11l-6-4"/>
                          </svg>
                        </div>
                        <div>
                          <h4 style={{
                            color: '#1e40af',
                            fontSize: '18px',
                            fontWeight: '600',
                            margin: '0 0 4px 0'
                          }}>
                            Verified Manufacturer
                          </h4>
                          <p style={{
                            color: '#1e293b',
                            fontSize: '16px',
                            fontWeight: '600',
                            margin: '0 0 4px 0'
                          }}>
                            {result.manufacturer.name}
                          </p>
                          <p style={{
                            color: '#3b82f6',
                            fontSize: '14px',
                            margin: 0,
                            fontFamily: 'monospace'
                          }}>
                            License: {result.manufacturer.licenseNumber}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                // COUNTERFEIT DETECTED - Enhanced Design
                <div>
                  <div style={{
                    background: 'linear-gradient(135deg, #fef2f2 0%, #fecaca 100%)',
                    border: '2px solid #ef4444',
                    borderRadius: '20px',
                    padding: '40px',
                    textAlign: 'center',
                    marginBottom: '32px'
                  }}>
                    <div style={{
                      width: '120px',
                      height: '120px',
                      background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                      borderRadius: '30px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 24px',
                      position: 'relative'
                    }}>
                      <div style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '120px',
                        height: '120px',
                        border: '3px solid #ef4444',
                        borderRadius: '30px',
                        opacity: 0.3
                      }} className="pulse-ring"></div>
                      
                      <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                        <circle cx="12" cy="12" r="10"/>
                        <line x1="15" y1="9" x2="9" y2="15"/>
                        <line x1="9" y1="9" x2="15" y2="15"/>
                      </svg>
                    </div>

                    <h2 style={{
                      color: '#dc2626',
                      fontSize: '2rem',
                      fontWeight: '800',
                      marginBottom: '12px',
                      letterSpacing: '-0.02em'
                    }}>
                      COUNTERFEIT DETECTED
                    </h2>
                    <p style={{
                      color: '#991b1b',
                      fontSize: '18px',
                      fontWeight: '500',
                      marginBottom: '16px'
                    }}>
                      This product is not authentic - DO NOT CONSUME
                    </p>
                    <div style={{
                      display: 'inline-block',
                      padding: '8px 20px',
                      background: 'rgba(239, 68, 68, 0.1)',
                      border: '1px solid rgba(239, 68, 68, 0.2)',
                      borderRadius: '50px',
                      color: '#dc2626',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}>
                      Verified by ScanOnce Enterprise
                    </div>
                  </div>

                  <div style={{
                    background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                    border: '1px solid #f59e0b',
                    borderRadius: '16px',
                    padding: '24px',
                    marginBottom: '24px'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      marginBottom: '12px'
                    }}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2">
                        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                        <line x1="12" y1="9" x2="12" y2="13"/>
                        <line x1="12" y1="17" x2="12.01" y2="17"/>
                      </svg>
                      <h4 style={{
                        color: '#92400e',
                        fontSize: '18px',
                        fontWeight: '600',
                        margin: 0
                      }}>
                        Warning Details
                      </h4>
                    </div>
                    <p style={{
                      color: '#92400e',
                      fontSize: '16px',
                      margin: 0,
                      lineHeight: '1.5'
                    }}>
                      <strong>Reason:</strong> {result.reason || 'Invalid authentication token - product not found in verified database'}
                    </p>
                  </div>

                  <div style={{
                    background: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
                    borderRadius: '16px',
                    padding: '24px'
                  }}>
                    <h4 style={{
                      color: '#475569',
                      fontSize: '18px',
                      fontWeight: '600',
                      marginBottom: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M9 11H5a2 2 0 0 0-2 2v3c0 1.1.9 2 2 2h4m6-6h4a2 2 0 0 1 2 2v3c0 1.1-.9 2-2 2h-4m-6 0a2 2 0 0 0-2-2v-3c0-1.1.9-2 2-2m6 0a2 2 0 0 1 2-2v-3c0-1.1-.9-2-2-2"/>
                      </svg>
                      Immediate Actions Required
                    </h4>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                      gap: '16px'
                    }}>
                      <div style={{
                        background: 'white',
                        padding: '16px',
                        borderRadius: '10px',
                        border: '1px solid #e2e8f0'
                      }}>
                        <div style={{ color: '#ef4444', fontSize: '16px', fontWeight: '600', marginBottom: '4px' }}>
                          Do Not Consume
                        </div>
                        <div style={{ color: '#64748b', fontSize: '14px' }}>
                          This product may be dangerous
                        </div>
                      </div>
                      <div style={{
                        background: 'white',
                        padding: '16px',
                        borderRadius: '10px',
                        border: '1px solid #e2e8f0'
                      }}>
                        <div style={{ color: '#3b82f6', fontSize: '16px', fontWeight: '600', marginBottom: '4px' }}>
                          Report Immediately
                        </div>
                        <div style={{ color: '#64748b', fontSize: '14px' }}>
                          Contact local authorities
                        </div>
                      </div>
                      <div style={{
                        background: 'white',
                        padding: '16px',
                        borderRadius: '10px',
                        border: '1px solid #e2e8f0'
                      }}>
                        <div style={{ color: '#8b5cf6', fontSize: '16px', fontWeight: '600', marginBottom: '4px' }}>
                          Preserve Evidence
                        </div>
                        <div style={{ color: '#64748b', fontSize: '14px' }}>
                          Keep product for investigation
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Verification Timestamp */}
              <div style={{
                marginTop: '32px',
                textAlign: 'center',
                padding: '20px',
                background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                borderRadius: '12px',
                border: '1px solid #e2e8f0'
              }}>
                <p style={{
                  color: '#64748b',
                  fontSize: '14px',
                  margin: '0 0 8px 0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <polyline points="12,6 12,12 16,14"/>
                  </svg>
                  Verified: {result.verificationTime ? new Date(result.verificationTime).toLocaleString() : new Date().toLocaleString()}
                </p>
                <p style={{
                  color: '#3b82f6',
                  fontSize: '12px',
                  margin: 0,
                  fontWeight: '600'
                }}>
                  Powered by ScanOnce Enterprise Authentication System
                </p>
              </div>

              {/* Action Buttons */}
              <div style={{
                display: 'flex',
                gap: '16px',
                justifyContent: 'center',
                marginTop: '32px',
                flexWrap: 'wrap'
              }}>
                <button
                  onClick={resetVerification}
                  style={{
                    background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
                    color: 'white',
                    padding: '16px 32px',
                    border: 'none',
                    borderRadius: '12px',
                    fontWeight: '600',
                    fontSize: '16px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/>
                    <path d="M21 3v5h-5"/>
                    <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/>
                    <path d="M8 16H3v5"/>
                  </svg>
                  Verify Another Product
                </button>

                {!result.isAuthentic && (
                  <a
                    href="mailto:karuoyamercy@gmail.com?subject=Counterfeit Product Report"
                    style={{
                      backgroundColor: '#ef4444',
                      color: 'white',
                      padding: '16px 32px',
                      textDecoration: 'none',
                      borderRadius: '12px',
                      fontWeight: '600',
                      fontSize: '16px',
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                      <polyline points="22,6 12,13 2,6"/>
                    </svg>
                    Report Counterfeit
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Instructions (when no result) */}
          {!result && !isLoading && step === 1 && (
            <div className="fade-in" style={{
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '24px',
              padding: '48px',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              marginTop: '40px'
            }}>
              <h3 style={{
                color: '#1e293b',
                fontSize: '24px',
                fontWeight: '600',
                marginBottom: '32px',
                textAlign: 'center'
              }}>
                How Product Verification Works
              </h3>
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                gap: '32px'
              }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{
                    width: '80px',
                    height: '80px',
                    background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
                    borderRadius: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 20px'
                  }}>
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                      <circle cx="12" cy="13" r="4"/>
                    </svg>
                  </div>
                  <h4 style={{
                    color: '#1e293b',
                    fontSize: '18px',
                    fontWeight: '600',
                    marginBottom: '12px'
                  }}>
                    Scan or Enter
                  </h4>
                  <p style={{
                    color: '#64748b',
                    fontSize: '14px',
                    lineHeight: '1.5'
                  }}>
                    Use camera to scan barcode or enter verification token manually
                  </p>
                </div>
                
                <div style={{ textAlign: 'center' }}>
                  <div style={{
                    width: '80px',
                    height: '80px',
                    background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                    borderRadius: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 20px'
                  }}>
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                      <polyline points="22,4 12,14.01 9,11.01"/>
                    </svg>
                  </div>
                  <h4 style={{
                    color: '#1e293b',
                    fontSize: '18px',
                    fontWeight: '600',
                    marginBottom: '12px'
                  }}>
                    Instant Verification
                  </h4>
                  <p style={{
                    color: '#64748b',
                    fontSize: '14px',
                    lineHeight: '1.5'
                  }}>
                    Get immediate results with detailed product information and authenticity status
                  </p>
                </div>
                
                <div style={{ textAlign: 'center' }}>
                  <div style={{
                    width: '80px',
                    height: '80px',
                    background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                    borderRadius: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 20px'
                  }}>
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                      <path d="M9 12l2 2 4-4"/>
                    </svg>
                  </div>
                  <h4 style={{
                    color: '#1e293b',
                    fontSize: '18px',
                    fontWeight: '600',
                    marginBottom: '12px'
                  }}>
                    Stay Protected
                  </h4>
                  <p style={{
                    color: '#64748b',
                    fontSize: '14px',
                                        lineHeight: '1.5'
                  }}>
                    Make informed decisions about product safety and authenticity before consumption
                  </p>
                </div>
              </div>

              {/* Trust Indicators */}
              <div style={{
                marginTop: '40px',
                paddingTop: '32px',
                borderTop: '1px solid #e2e8f0',
                display: 'flex',
                justifyContent: 'center',
                gap: '48px',
                flexWrap: 'wrap'
              }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{
                    color: '#1e293b',
                    fontSize: '24px',
                    fontWeight: '700',
                    marginBottom: '4px'
                  }}>
                    99.9%
                  </div>
                  <div style={{
                    color: '#64748b',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}>
                    Accuracy Rate
                  </div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{
                    color: '#1e293b',
                    fontSize: '24px',
                    fontWeight: '700',
                    marginBottom: '4px'
                  }}>
                    &lt;50ms
                  </div>
                  <div style={{
                    color: '#64748b',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}>
                    Response Time
                  </div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{
                    color: '#1e293b',
                    fontSize: '24px',
                    fontWeight: '700',
                    marginBottom: '4px'
                  }}>
                    24/7
                  </div>
                  <div style={{
                    color: '#64748b',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}>
                    Available
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyPage;

